<template>
    <div>
      <!-- User selector + save/clear actions on one row -->
      <div class="row g-2 mb-3 align-items-end">
        <div class="col-12 col-md-4 col-lg-3" style="padding-top: .1em">
          <span class="input-title">{{ $t('common.duty_officer') }}</span>
          <multiselect
            v-model="user"
            :placeholder="$t('trip_list.select_user')"
            :deselect-label="$t('common.remove_selection')"
            :select-label="$t('common.select')"
            :close-on-select="true"
            :multiple="false"
            label="name"
            :custom-label="userName"
            track-by="id"
            :searchable="true"
            :internal-search="true"
            :selected-label="$t('common.selected')"
            :options="users"
          />
        </div>
        <div class="col-12 col-md text-md-end">
          <button
            class="form-button btn btn-sm btn-secondary"
            @click.stop="clearForm"
          >
            {{ $t('common.clear') }}
          </button>
          <button
            class="form-button btn btn-sm btn-success"
            :disabled="!isFormFilled"
            @click.stop="saveSchedule"
          >
            {{ $t('common.save') }}
          </button>
        </div>
      </div>

      <!-- Schedule rows below the user selector -->
      <!-- Column headers (hidden on mobile, where each field is labelled inline) -->
      <div class="row g-2 d-none d-md-flex">
        <div class="col">
          <span class="input-title">{{ $t('common.start_date') }}</span>
        </div>
        <div class="col">
          <span class="input-title">{{ $t('common.end_date') }}</span>
        </div>
        <div class="col-md-auto" />
      </div>
      <div
        v-for="(row, index) in scheduledPeriods"
        :key="'item' + index"
        class="row g-2 align-items-end mb-3"
      >
        <div class="col-12 col-md">
            <span class="input-title d-md-none">{{ $t('common.start_date') }}</span>
            <date-time-picker
              v-model:date="row.start_date"
              class="routa-input"
              format="dd.MM.yyyy HH:mm"
              :enableTimePicker="true"
            />
        </div>
        <div class="col-12 col-md">
            <span class="input-title d-md-none">{{ $t('common.end_date') }}</span>
            <date-time-picker
              v-model:date="row.end_date"
              class="routa-input"
              format="dd.MM.yyyy HH:mm"
              :enableTimePicker="true"
              :minDate="row.start_date"
            />
        </div>
        <div class="col-12 col-md-auto text-md-end">
          <button
            class="btn btn-sm btn-danger"
            :disabled="scheduledPeriods.length <= 1 && row.id == null"
            :title="$t('common.remove')"
            @click.stop="confirmRemove(index)"
          >
            <i
              class="fa fa-trash"
              style="font-size: .8em; padding: 0; line-height: 1em;"
            />
          </button>
        </div>
      </div>
    </div>
    <div class="mt-2">
      <button
        class="form-button btn btn-sm btn-primary"
        @click.stop="addDate"
      >
        {{ $t('common.add_new') }}
      </button>
  </div>

    <!-- Delete confirmation -->
    <base-modal
      v-if="pendingDeleteIndex !== null"
      size="sm"
      @ok="performRemove()"
      @close="cancelRemove()"
    >
      <div class="modal-body">
        <span>{{ $t('common.confirm_delete') }}</span>
      </div>
    </base-modal>
</template>

<script>
import DateTimePicker from "@/components/common/DateTimePicker";
import BaseModal from "@/components/BaseModal.vue";

export default {
    name: "ContractDatePicker",
    components: { DateTimePicker, BaseModal },
    props: {
        index: {
            type: Number,
            default: null
        },
        dutyOfficerSchedules: {
            type: Array,
            default() {
                return []
            }
        },
        users: {
            type: Array,
            default() {
                return []
            }
        },
        // When set from the calendar ({ user, schedules }), pre-fills the form for editing
        selectedSchedule: {
            type: Object,
            default: null
        }
    },
    emits: ['save', 'delete-period'],
    data() {
        return {
            scheduledPeriods: [{}],
            user: null,
            pendingDeleteIndex: null,
        };
    },
    computed: {
        isFormFilled() {
            let formFilled = true
            for (const schedule of this.scheduledPeriods) {
                const filled = !!schedule.start_date && !!schedule.end_date
                if (!filled) {
                    formFilled = false
                    break
                }
            }

            return !!this.user && formFilled
        },
    },
    watch: {
        selectedSchedule: {
            immediate: true,
            handler(value) {
                this.loadSelected(value)
            }
        }
    },
    methods: {
        // Load a user + their existing schedules from the calendar into the form
        loadSelected(value) {
            if (!value || !value.user) {
                return
            }
            this.user = {
                id: value.user.id,
                first_name: value.user.first_name,
                last_name: value.user.last_name,
            }
            const periods = (value.schedules || []).map(s => ({
                id: s.id,
                start_date: s.start_date,
                end_date: s.end_date,
            }))
            this.scheduledPeriods = periods.length ? periods : [{}]
        },
        saveSchedule() {
            const data = { 'user': this.user, 'dates': this.scheduledPeriods };
            this.$emit('save', data);
        },
        clearForm() {
            this.user = null
            this.scheduledPeriods = [{}]
        },
        reloadPeriods(schedules) {
            const periods = (schedules || []).map(s => ({
                id: s.id,
                start_date: s.start_date,
                end_date: s.end_date,
            }))
            this.scheduledPeriods = periods.length ? periods : [{}]
        },
        userName({first_name, last_name}) {
            return `${first_name}  ${last_name}`;
        },
        // Open the delete confirmation for the given period row
        confirmRemove(index) {
            this.pendingDeleteIndex = index
        },
        cancelRemove() {
            this.pendingDeleteIndex = null
        },
        // Confirmed delete: remove the period (and delete on backend if it was saved)
        performRemove() {
            const index = this.pendingDeleteIndex
            if (index === null) {
                return
            }
            const period = this.scheduledPeriods[index]
            // Existing (saved) period -> ask parent to delete it on the backend
            if (period && period.id != null) {
                this.$emit('delete-period', period.id)
            }
            this.scheduledPeriods.splice(index, 1);
            if (this.scheduledPeriods.length === 0) {
                this.scheduledPeriods = [{}]
            }
            this.pendingDeleteIndex = null
        },
        addDate() {
            this.scheduledPeriods.push({})
        }

    }
}
</script>

<style scoped>
.input-title {
    font-size: .9em;
    color: #404041;
}
</style>

